<template>
  <div class="global-data-filter">
    <div class="filter-container">
      <div class="filter-actions">
        <el-button
          type="primary"
          size="small"
          @click="applyFilters"
          :disabled="!hasActiveFilters"
        >
          Apply Filter
        </el-button>
        <el-button
          type="default"
          size="small"
          @click="addFilter"
          :disabled="filters.length >= 10"
        >
          Add Filter
        </el-button>
        <el-button
          v-if="hasActiveFilters"
          type="text"
          size="small"
          @click="clearAllFilters"
        >
          Clear All
        </el-button>
      </div>

      <div class="filter-items">
        <div v-for="(filter, index) in filters" :key="index" class="filter-row">
          <el-select
            v-model="filter.column"
            placeholder="Select column"
            size="small"
            class="filter-column-select"
          >
            <el-option
              v-for="col in filterableColumns"
              :key="col"
              :label="col"
              :value="col"
            >
            </el-option>
          </el-select>

          <el-select
            v-if="isNumericColumn(filter.column)"
            v-model="filter.operator"
            placeholder="Operator"
            size="small"
            class="filter-operator-select"
          >
            <el-option label="=" value="="></el-option>
            <el-option label=">" value=">"></el-option>
            <el-option label=">=" value=">="></el-option>
            <el-option label="<" value="<"></el-option>
            <el-option label="<=" value="<="></el-option>
          </el-select>

          <el-input
            v-model="filter.value"
            placeholder="Value"
            size="small"
            class="filter-value-input"
          >
          </el-input>

          <el-button
            icon="el-icon-delete"
            type="text"
            size="small"
            @click="removeFilter(index)"
          >
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BackTestDataFilter',
  props: {
    availableColumns: {
      type: Array,
      default: () => []
    },
    data: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      filters: [
        {
          column: '',
          operator: '=',
          value: ''
        }
      ],
      ignoredColumnsLower: [
        'localtimestamp',
        'timestamp',
        'contractorid',
        'podmac',
        'podstatus',
        'nodemac',
        'hwversion',
        'firmwareversion',
        'isnewpad',
        'isnewredalert'
      ]
    }
  },
  computed: {
    filterableColumns() {
      // Filter out ignored columns
      return this.availableColumns.filter(column => {
        const columnLower = String(column || '').toLowerCase()
        return !this.ignoredColumnsLower.includes(columnLower)
      })
    },
    hasActiveFilters() {
      return this.filters.some(f => f.column && f.value)
    }
  },
  methods: {
    isNumericColumn(column) {
      if (!column) return false

      // If we have data, check sample values
      if (this.data && this.data.length > 0) {
        const sample = this.data.slice(0, 10)
        return sample.every(row => {
          const value = row[column]
          if (value === null || value === undefined || value === '') return true
          return !isNaN(parseFloat(value))
        })
      }

      // If no data, assume all columns could be numeric (show operators)
      return true
    },
    addFilter() {
      if (this.filters.length < 10) {
        this.filters.push({
          column: '',
          operator: '=',
          value: ''
        })
      }
    },
    removeFilter(index) {
      this.filters.splice(index, 1)
      if (this.filters.length === 0) {
        this.filters.push({
          column: '',
          operator: '=',
          value: ''
        })
      }
    },
    clearAllFilters() {
      this.filters = [
        {
          column: '',
          operator: '=',
          value: ''
        }
      ]
      this.emitFilters()
    },
    applyFilters() {
      this.emitFilters()
    },
    emitFilters() {
      // Only emit active filters (those with both column and value)
      const activeFilters = this.filters.filter(f => f.column && f.value)
      this.$emit('filter-change', activeFilters)
    },
    getFilteredData() {
      let result = this.data

      for (const filter of this.filters) {
        if (!filter.column || !filter.value) continue

        if (this.isNumericColumn(filter.column)) {
          // Numeric filtering
          const numValue = parseFloat(filter.value)
          result = result.filter(row => {
            const cellValue = parseFloat(row[filter.column])
            if (isNaN(cellValue)) return true // Keep rows with non-numeric values

            switch (filter.operator) {
              case '=':
                return cellValue === numValue
              case '>':
                return cellValue > numValue
              case '>=':
                return cellValue >= numValue
              case '<':
                return cellValue < numValue
              case '<=':
                return cellValue <= numValue
              default:
                return true
            }
          })
        } else {
          // String filtering
          result = result.filter(row => {
            const cellValue = String(row[filter.column] || '').toLowerCase()
            return cellValue.includes(filter.value.toLowerCase())
          })
        }
      }

      return result
    }
  }
}
</script>

<style scoped lang="scss">
.global-data-filter {
  margin-bottom: 15px;
  padding: 12px;
  background-color: #f5f7fa;
  border: 1px solid #dcdfe6;
  border-radius: 4px;

  .filter-container {
    .filter-actions {
      display: flex;
      gap: 8px;
      margin-bottom: 10px;
      align-items: center;
    }

    .filter-items {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .filter-row {
        display: flex;
        gap: 8px;
        align-items: center;

        .filter-column-select {
          flex: 1;
          min-width: 150px;
        }

        .filter-operator-select {
          width: 70px;
        }

        .filter-value-input {
          flex: 1;
          min-width: 100px;
        }
      }
    }
  }
}
</style>
